// src/hooks/useCart.js - Local-First Cart Hook
/**
 * useCart - React hook for local-first cart operations
 * 
 * Replaces:
 * - useCartQuery
 * - useCartMutations
 * - useCartOperations
 * 
 * Features:
 * - Instant operations (localStorage)
 * - Automatic re-renders on changes
 * - Background sync to server
 * - Offline-first design
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import cartManager from '../services/CartManager';
import syncService from '../services/SyncService';

/**
 * Main cart hook
 * @returns {Object} Cart state and operations
 */
export function useCart() {
  // Local state for cart data
  const [cart, setCart] = useState(() => cartManager.getCart());
  const [error, setError] = useState(null);

  // Subscribe to cart changes
  useEffect(() => {
    // Read fresh cart data on mount (in case localStorage was updated before subscribe)
    setCart(cartManager.getCart());

    const unsubscribe = cartManager.subscribe(() => {
      const newCart = cartManager.getCart();
      setCart(newCart);
    });

    // Listen for localStorage changes from other tabs/windows
    const handleStorageChange = (e) => {
      if (e.key === 'cart_data') {
        setCart(cartManager.getCart());
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Note: Authentication changes are now handled globally in App.js
  // This ensures cart sync happens regardless of which components are mounted

  // === Cart Operations ===

  /**
   * Add item to cart
   * @param {Object} product - Product object
   * @param {Object} unit - Unit object
   * @param {number} quantity - Quantity to add
   * @param {number} price - Price override (optional)
   * @returns {Promise<Object>} Added/updated item
   */
  const addItem = useCallback(async (product, unit, quantity = 1, price = null) => {
    try {
      setError(null);
      
      if (!product?.id || !unit?.id) {
        throw new Error('Invalid product or unit');
      }

      const item = cartManager.addItem(product, unit, quantity, price);
      return item;
    } catch (err) {
      setError(err.message);
      console.error('[useCart] Error adding item:', err);
      throw err;
    }
  }, []);

  /**
   * Update item quantity
   * @param {string} itemId - Item ID
   * @param {number} newQuantity - New quantity
   * @returns {Promise<Object|null>} Updated item or null if removed
   */
  const updateItem = useCallback(async (itemId, newQuantity) => {
    try {
      setError(null);
      
      if (!itemId) {
        throw new Error('Item ID is required');
      }

      const item = cartManager.updateItem(itemId, newQuantity);
      return item;
    } catch (err) {
      setError(err.message);
      console.error('[useCart] Error updating item:', err);
      throw err;
    }
  }, []);

  /**
   * Set item quantity directly (not add)
   * @param {Object} product - Product object
   * @param {Object} unit - Unit object
   * @param {number} quantity - Quantity to set
   * @param {number} price - Price override (optional)
   * @returns {Promise<Object|null>} Updated item or null
   */
  const setItemQuantity = useCallback(async (product, unit, quantity, price = null) => {
    try {
      setError(null);

      if (!product?.id || !unit?.id) {
        throw new Error('Invalid product or unit');
      }

      const item = cartManager.setItemQuantity(product, unit, quantity, price);
      return item;
    } catch (err) {
      setError(err.message);
      console.error('[useCart] Error setting item quantity:', err);
      throw err;
    }
  }, []);

  /**
   * Remove item from cart
   * @param {string} itemId - Item ID
   * @returns {Promise<boolean>} Success status
   */
  const removeItem = useCallback(async (itemId) => {
    try {
      setError(null);
      
      if (!itemId) {
        throw new Error('Item ID is required');
      }

      const success = cartManager.removeItem(itemId);
      return success;
    } catch (err) {
      setError(err.message);
      console.error('[useCart] Error removing item:', err);
      throw err;
    }
  }, []);

  /**
   * Clear entire cart
   * @returns {Promise<boolean>} Success status
   */
  const clearCart = useCallback(async () => {
    try {
      setError(null);
      const success = cartManager.clearCart();
      return success;
    } catch (err) {
      setError(err.message);
      console.error('[useCart] Error clearing cart:', err);
      throw err;
    }
  }, []);

  // === Helper Functions ===

  /**
   * Get specific cart item by product and unit
   * @param {number} productId - Product ID
   * @param {string} unitSymbol - Unit symbol
   * @returns {Object|null} Cart item or null
   */
  const getCartItem = useCallback((productId, unitSymbol) => {
    if (!cart?.items || !Array.isArray(cart.items)) return null;
    
    return cart.items.find(
      item => item.product?.id === productId && item.unit?.symbol === unitSymbol
    );
  }, [cart?.items]);

  /**
   * Get quantity in cart for specific product and unit
   * @param {number} productId - Product ID
   * @param {string} unitSymbol - Unit symbol
   * @returns {number} Quantity in cart
   */
  const getQuantityInCart = useCallback((productId, unitSymbol) => {
    const item = getCartItem(productId, unitSymbol);
    return item ? parseFloat(item.quantity || 0) : 0;
  }, [getCartItem]);

  /**
   * Check if product has any units in cart
   * @param {number} productId - Product ID
   * @returns {boolean} True if product is in cart
   */
  const isProductInCart = useCallback((productId) => {
    if (!cart?.items || !Array.isArray(cart.items)) return false;
    
    return cart.items.some(item => item.product?.id === productId);
  }, [cart?.items]);

  /**
   * Get total quantity for a product (all units)
   * @param {number} productId - Product ID
   * @returns {number} Total quantity across all units
   */
  const getProductTotalQuantity = useCallback((productId) => {
    if (!cart?.items || !Array.isArray(cart.items)) return 0;
    
    return cart.items
      .filter(item => item.product?.id === productId)
      .reduce((total, item) => total + parseFloat(item.quantity || 0), 0);
  }, [cart?.items]);

  /**
   * Check if item is pending (optimistic update)
   * @param {string} itemId - Item ID
   * @returns {boolean} True if item is pending
   */
  const isItemPending = useCallback((itemId) => {
    if (!cart?.items || !Array.isArray(cart.items)) return false;
    const item = cart.items.find(item => item.id === itemId);
    return item?.is_pending === true;
  }, [cart?.items]);

  /**
   * Check if item failed to sync
   * @param {string} itemId - Item ID
   * @returns {boolean} True if item failed
   */
  const isItemFailed = useCallback((itemId) => {
    if (!cart?.items || !Array.isArray(cart.items)) return false;
    const item = cart.items.find(item => item.id === itemId);
    return item?.is_failed === true;
  }, [cart?.items]);

  /**
   * Get error message for failed item
   * @param {string} itemId - Item ID
   * @returns {string|null} Error message or null
   */
  const getItemError = useCallback((itemId) => {
    if (!cart?.items || !Array.isArray(cart.items)) return null;
    const item = cart.items.find(item => item.id === itemId);
    return item?.error_message || null;
  }, [cart?.items]);

  // === Derived State ===

  const cartState = useMemo(() => ({
    // Cart data
    cartId: cart?.id || null,
    items: cart?.items || [],
    totalPrice: parseFloat(cart?.total_price || 0),
    totalItemsCount: cart?.total_items_count || 0,
    updatedAt: cart?.updated_at_iso,
    
    // Status flags
    isEmpty: !cart?.items?.length,
    isLoaded: !!cart,
    hasItems: cart?.items?.length > 0,
    hasPendingItems: cart?.items?.some(item => item.is_pending),
    hasFailedItems: cart?.items?.some(item => item.is_failed),
    
    // Counts
    itemCount: cart?.items?.length || 0,
    totalQuantity: (cart?.items || []).reduce((sum, item) => sum + parseFloat(item.quantity || 0), 0)
  }), [cart]);

  // === Return Hook Interface ===
  
  return {
    // Cart state
    ...cartState,
    error,

    // Operations (all instant)
    addItem,
    updateItem,
    setItemQuantity,
    removeItem,
    clearCart,

    // Helper functions
    getCartItem,
    getQuantityInCart,
    isProductInCart,
    getProductTotalQuantity,

    // Sync status helpers
    isItemPending,
    isItemFailed,
    getItemError,

    // Sync status
    syncStatus: syncService.getStatus(),

    // Sync controls
    forceSync: syncService.forceSync.bind(syncService),
    refreshFromServer: syncService.refreshFromServer.bind(syncService),
    refreshDiscounts: syncService.refreshDiscounts.bind(syncService),

    // Discount calculation (immediate, no sync needed)
    calculateDiscounts: cartManager.calculateDiscounts.bind(cartManager)
  };
}

/**
 * Lightweight cart status hook for components that only need basic info
 * @returns {Object} Basic cart status
 */
export function useCartStatus() {
  const [cart, setCart] = useState(() => cartManager.getCart());

  useEffect(() => {
    // Read fresh cart data on mount
    setCart(cartManager.getCart());

    const unsubscribe = cartManager.subscribe(() => {
      const newCart = cartManager.getCart();
      setCart(newCart);
    });

    // Listen for localStorage changes from other tabs/windows
    const handleStorageChange = (e) => {
      if (e.key === 'cart_data') {
        setCart(cartManager.getCart());
      }
    };
    window.addEventListener('storage', handleStorageChange);

    return () => {
      unsubscribe();
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  return useMemo(() => ({
    itemCount: cart?.total_items_count || 0,
    totalPrice: parseFloat(cart?.total_price || 0),
    isEmpty: !cart?.items?.length,
    hasItems: cart?.items?.length > 0,
    isLoaded: !!cart,
    timestamp: cart?.updated_at_iso
  }), [cart]);
}

/**
 * Cart operations hook for components that only need operations
 * @returns {Object} Cart operations only
 */
export function useCartOperations() {
  const { addItem, updateItem, removeItem, clearCart, getCartItem, getQuantityInCart } = useCart();
  
  return {
    addItem,
    updateItem,
    removeItem,
    clearCart,
    getCartItem,
    getQuantityInCart
  };
}

/**
 * Cart sync hook for monitoring synchronization status
 * NOTE: Removed polling interval - was creating 1-second timer in EVERY component!
 * Status updates now happen via events from SyncService
 * @returns {Object} Sync status and controls
 */
export function useCartSync() {
  const [syncStatus, setSyncStatus] = useState(() => syncService.getStatus());

  useEffect(() => {
    // Subscribe to sync status changes via event emitter instead of polling
    const handleStatusChange = () => {
      setSyncStatus(syncService.getStatus());
    };

    // Check if SyncService has event emitter, otherwise just get initial status
    if (syncService.on) {
      syncService.on('statusChange', handleStatusChange);
      return () => syncService.off('statusChange', handleStatusChange);
    }

    // Fallback: Get status once on mount (no polling!)
    return () => {};
  }, []);

  return {
    ...syncStatus,
    forceSync: syncService.forceSync.bind(syncService),
    updateConfig: syncService.updateConfig.bind(syncService)
  };
}

// Export default
export default useCart;